import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  ContentPlanScope,
  PodcastWithPrograms,
} from "../services/api";

export type ContentPlanScopeRow = ContentPlanScope;

type BrandOption = { _id: string; name: string; mentionCount?: number };

/** Loose match so "coca cola" matches "Coca-Cola". */
function normalizeBrandSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function brandMatchesFilter(name: string, filter: string): boolean {
  const q = normalizeBrandSearch(filter);
  if (!q) return true;
  return normalizeBrandSearch(name).includes(q);
}

function blankScope(): ContentPlanScopeRow {
  return { podcastId: "", programIds: [], brandIds: [] };
}

function programIdsKey(programIds?: string[]): string {
  return (programIds ?? []).slice().sort().join(",");
}

interface ScopeBrandPickerProps {
  podcastId: string;
  programIds?: string[];
  brandIds: string[];
  disabled?: boolean;
  onChange: (brandIds: string[]) => void;
}

function ScopeBrandPicker({
  podcastId,
  programIds,
  brandIds,
  disabled,
  onChange,
}: ScopeBrandPickerProps) {
  const [filter, setFilter] = useState("");
  const programKey = programIdsKey(programIds);

  useEffect(() => {
    setFilter("");
  }, [podcastId, programKey]);

  const { data: presentBrands, isLoading, isError } = useQuery({
    queryKey: ["podcastPresentBrands", podcastId, programKey],
    queryFn: () =>
      apiService.getPodcastPresentBrands(podcastId, {
        programIds:
          programIds && programIds.length > 0 ? programIds : undefined,
      }),
    enabled: !!podcastId,
    staleTime: 2 * 60_000,
  });

  const allOptions: BrandOption[] = useMemo(() => {
    const merged = new Map<string, BrandOption>();
    for (const b of presentBrands ?? []) {
      merged.set(b._id, {
        _id: b._id,
        name: b.name,
        mentionCount: b.mentionCount,
      });
    }
    for (const id of brandIds) {
      if (!merged.has(id)) merged.set(id, { _id: id, name: id });
    }
    return Array.from(merged.values()).sort((a, b) =>
      a.name.localeCompare(b.name, "es"),
    );
  }, [presentBrands, brandIds]);

  const filteredOptions = useMemo(() => {
    if (!filter.trim()) return allOptions;
    return allOptions.filter((b) => brandMatchesFilter(b.name, filter));
  }, [allOptions, filter]);

  const selected = useMemo(
    () => allOptions.filter((b) => brandIds.includes(b._id)),
    [allOptions, brandIds],
  );

  const brandHelperBase =
    programIds && programIds.length > 0
      ? "Marcas con menciones en los programas seleccionados (últimos 90 días)"
      : "Marcas con menciones (últimos 90 días)";
  const brandHelperSuffix =
    !isLoading && !isError && presentBrands
      ? ` · ${presentBrands.length} encontrada${presentBrands.length === 1 ? "" : "s"}`
      : "";

  return (
    <Autocomplete
      multiple
      options={filteredOptions}
      getOptionLabel={(o) =>
        o.mentionCount != null && o.mentionCount > 0
          ? `${o.name} (${o.mentionCount} menc.)`
          : o.name
      }
      value={selected}
      disabled={disabled || !podcastId}
      loading={isLoading}
      inputValue={filter}
      onInputChange={(_, v, reason) => {
        if (reason === "input" || reason === "clear") setFilter(v);
        if (reason === "reset") setFilter("");
      }}
      onChange={(_, v) => onChange(v.map((b) => b._id))}
      filterOptions={(x) => x}
      noOptionsText={
        isLoading
          ? "Cargando marcas…"
          : isError
            ? "Error al cargar marcas"
            : filter.trim()
              ? "Sin coincidencias"
              : "No hay menciones en los últimos 90 días"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Marcas a copiar (vacío = todas)"
          size="small"
          placeholder="Filtrar marcas del podcast…"
          helperText={`${brandHelperBase}${brandHelperSuffix}`}
        />
      )}
      isOptionEqualToValue={(o, v) => o._id === v._id}
    />
  );
}

type HandleDraft = { instagram: string; tiktok: string; facebook: string };

function handlesFromPodcast(podcast: PodcastWithPrograms): HandleDraft {
  return {
    instagram: podcast.socialHandles?.instagram || "",
    tiktok: podcast.socialHandles?.tiktok || "",
    facebook: podcast.socialHandles?.facebook || "",
  };
}

function ScopeApifyHandles({
  companyId,
  podcast,
  disabled,
}: {
  companyId: string;
  podcast: PodcastWithPrograms;
  disabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const saved = handlesFromPodcast(podcast);
  const [draft, setDraft] = useState<HandleDraft>(saved);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(handlesFromPodcast(podcast));
    setError(null);
  }, [podcast._id, podcast.socialHandles?.instagram, podcast.socialHandles?.tiktok, podcast.socialHandles?.facebook]);

  const dirty =
    draft.instagram.trim() !== saved.instagram.trim() ||
    draft.tiktok.trim() !== saved.tiktok.trim() ||
    draft.facebook.trim() !== saved.facebook.trim();

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updatePodcastSocialAccounts(podcast._id, {
        instagram: draft.instagram.trim() || null,
        tiktok: draft.tiktok.trim() || null,
        facebook: draft.facebook.trim() || null,
      }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({
        queryKey: ["companyPodcastsWithPrograms", companyId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["company-podcasts-dropdown", companyId],
      });
    },
    onError: (e: any) => {
      setError(
        e?.response?.data?.message || e?.message || "Error guardando handles Apify",
      );
    },
  });

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Handles Apify — IG/TikTok: username sin @. Facebook: Page o URL.
        </Typography>
        {podcast.hasConnectedAccounts ? (
          <Chip label="OAuth conectado" size="small" color="success" variant="outlined" />
        ) : podcast.canApifyFallback || draft.instagram || draft.tiktok || draft.facebook ? (
          <Chip label="Apify (handles)" size="small" color="info" variant="outlined" />
        ) : (
          <Chip label="Sin OAuth ni handles" size="small" color="warning" variant="outlined" />
        )}
      </Stack>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <TextField
          size="small"
          label="Instagram"
          placeholder="usuario"
          value={draft.instagram}
          disabled={disabled}
          onChange={(e) => setDraft((d) => ({ ...d, instagram: e.target.value }))}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="TikTok"
          placeholder="usuario"
          value={draft.tiktok}
          disabled={disabled}
          onChange={(e) => setDraft((d) => ({ ...d, tiktok: e.target.value }))}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="Facebook"
          placeholder="pagename o URL"
          value={draft.facebook}
          disabled={disabled}
          onChange={(e) => setDraft((d) => ({ ...d, facebook: e.target.value }))}
          sx={{ flex: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={disabled || !dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          sx={{ alignSelf: { xs: "stretch", md: "center" }, whiteSpace: "nowrap" }}
        >
          {saveMutation.isPending ? "Guardando…" : "Guardar handles"}
        </Button>
      </Stack>
    </Stack>
  );
}

export interface ContentPlanScopesEditorProps {
  companyId: string;
  scopes: ContentPlanScopeRow[];
  onChange: (scopes: ContentPlanScopeRow[]) => void;
  showBrands?: boolean;
  showSocialStatus?: boolean;
  /** When true, lists all assignable podcasts (not only those linked to the company). */
  includeAssignablePodcasts?: boolean;
  disabled?: boolean;
}

export function ContentPlanScopesEditor({
  companyId,
  scopes,
  onChange,
  showBrands = false,
  showSocialStatus = false,
  includeAssignablePodcasts = false,
  disabled = false,
}: ContentPlanScopesEditorProps) {
  const {
    data: podcastsWithPrograms,
    isLoading: podcastsLoading,
    isError: podcastsError,
  } = useQuery({
    queryKey: ["companyPodcastsWithPrograms", companyId, includeAssignablePodcasts],
    queryFn: () =>
      apiService.getCompanyPodcastsWithPrograms(companyId, {
        includeAssignable: includeAssignablePodcasts,
      }),
    enabled: !!companyId,
  });

  const podcastById = useMemo(() => {
    const map = new Map<string, PodcastWithPrograms>();
    (podcastsWithPrograms ?? []).forEach((p) => map.set(p._id, p));
    return map;
  }, [podcastsWithPrograms]);

  if (!companyId) {
    return <Alert severity="info">Selecciona una empresa para editar el alcance.</Alert>;
  }

  if (podcastsLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Cargando podcasts…
        </Typography>
      </Stack>
    );
  }

  if (podcastsError) {
    return (
      <Alert severity="error">
        No se pudieron cargar los podcasts. Verifica que estés logueado como admin.
      </Alert>
    );
  }

  if ((podcastsWithPrograms ?? []).length === 0) {
    return (
      <Alert severity="warning">
        No hay podcasts disponibles. Crea o importa podcasts antes de configurar el pipeline.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="subtitle2">Podcast → Programas{showBrands ? " → Marcas" : ""}</Typography>
        <Typography variant="caption" color="text.secondary">
          Programas vacíos = todos los programas del podcast.
          {showBrands ? " Marcas vacías = todas las menciones detectadas." : ""}
        </Typography>
        <Box flex={1} />
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange([...scopes, blankScope()])}
          disabled={disabled}
        >
          Agregar podcast
        </Button>
      </Box>

      {scopes.map((scope, idx) => {
        const podcast = podcastById.get(scope.podcastId) ?? null;
        const programOptions = podcast?.programs ?? [];
        const selectedPrograms = programOptions.filter((p) =>
          (scope.programIds ?? []).includes(p._id),
        );

        return (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={podcastsWithPrograms ?? []}
                  getOptionLabel={(o) => o.title}
                  value={podcast}
                  disabled={disabled}
                  filterSelectedOptions
                  onChange={(_, v) =>
                    onChange(
                      scopes.map((s, i) =>
                        i === idx
                          ? { ...s, podcastId: v?._id ?? "", programIds: [], brandIds: [] }
                          : s,
                      ),
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Podcast" required size="small" />
                  )}
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                />
                <IconButton
                  size="small"
                  color="error"
                  disabled={disabled}
                  onClick={() =>
                    onChange(
                      scopes.length === 1 ? [blankScope()] : scopes.filter((_, i) => i !== idx),
                    )
                  }
                  aria-label="Eliminar podcast"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Autocomplete
                multiple
                options={programOptions}
                getOptionLabel={(o) => o.name}
                value={selectedPrograms}
                disabled={disabled || !scope.podcastId}
                onChange={(_, v) =>
                  onChange(
                    scopes.map((s, i) =>
                      i === idx ? { ...s, programIds: v.map((p) => p._id), brandIds: [] } : s,
                    ),
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Programas (vacío = todos)"
                    size="small"
                  />
                )}
                isOptionEqualToValue={(o, v) => o._id === v._id}
              />

              {showBrands && (
                <ScopeBrandPicker
                  podcastId={scope.podcastId}
                  programIds={scope.programIds}
                  brandIds={scope.brandIds ?? []}
                  disabled={disabled}
                  onChange={(ids) =>
                    onChange(
                      scopes.map((s, i) => (i === idx ? { ...s, brandIds: ids } : s)),
                    )
                  }
                />
              )}

              {showSocialStatus && podcast && (
                <ScopeApifyHandles
                  companyId={companyId}
                  podcast={podcast}
                  disabled={disabled}
                />
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

export { blankScope as blankContentPlanScope };
